import { useEffect, useState } from "react";
import { useAuth } from "@/features/auth/auth-context";
import { toast } from "sonner";
import { getYouTubeAuthUrl, checkHasYoutubeToken } from "@/lib/youtube-auth";
import { initiateYouTubeAuth } from '@/lib/youtube-auth'

export function useYoutubeConnection() {
  const { user, session } = useAuth();
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkConnection = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        console.log('[useYoutubeConnection] 🔍 Checking YouTube connection for user:', user.id);
        const hasToken = await checkHasYoutubeToken(user.id);
        setConnected(hasToken);
        console.log('[useYoutubeConnection] 🔍 Connection status:', hasToken);
        
        // If just connected, trigger sync
        if (hasToken && !connected) {
          console.log('[useYoutubeConnection] 🔄 New connection detected, triggering sync...');
          triggerChannelSync();
        }
      } catch (err) {
        console.error('[useYoutubeConnection] ❌ Error checking connection:', err);
        setConnected(false);
      } finally {
        setLoading(false);
      }
    };

    checkConnection();
  }, [user]);

  // Helper function to trigger sync
  const triggerChannelSync = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/debug-manual-sync?force=true`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        console.log('[useYoutubeConnection] ✅ Channel sync triggered successfully');
      } else {
        console.warn('[useYoutubeConnection] ⚠️ Sync trigger failed:', response.status);
      }
    } catch (error) {
      console.error('[useYoutubeConnection] ❌ Failed to trigger sync:', error);
      // Don't show error to user - sync will happen automatically later
    }
  };

  const connect = () => {
    if (!user) {
      toast.error("Please sign in first");
      return;
    }

    try {
      console.log('[useYoutubeConnection] 🔄 Initiating YouTube OAuth...');
      initiateYouTubeAuth();
    } catch (error: any) {
      console.error('[useYoutubeConnection] ❌ Failed to initiate auth:', error);
      toast.error("Failed to start YouTube connection");
    }
  };

  const disconnect = async () => {
    if (!user || !session) {
      toast.error("Please sign in first");
      return;
    }

    try {
      console.log('[useYoutubeConnection] 🔄 Disconnecting YouTube...');
      
      // Delete youtube_tokens
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/youtube_tokens?user_id=eq.${user.id}`,
        {
          method: "DELETE",
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY!,
            Authorization: `Bearer ${session.access_token}`,
            Prefer: "return=representation",
          },
        }
      );

      if (!res.ok) {
        const error = await res.text();
        throw new Error(error);
      }

      // Delete channel_analytics to force fresh data on reconnect
      console.log('[useYoutubeConnection] 🧹 Clearing channel analytics...');
      const analyticsRes = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/channel_analytics?user_id=eq.${user.id}`,
        {
          method: "DELETE",
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY!,
            Authorization: `Bearer ${session.access_token}`,
            Prefer: "return=representation",
          },
        }
      );

      if (!analyticsRes.ok) {
        console.warn('[useYoutubeConnection] ⚠️ Failed to clear channel analytics (non-critical)');
      } else {
        console.log('[useYoutubeConnection] ✅ Channel analytics cleared');
      }

      console.log('[useYoutubeConnection] ✅ YouTube disconnected successfully');
      toast.success("YouTube account disconnected.");
      setConnected(false);
    } catch (err: any) {
      console.error('[useYoutubeConnection] ❌ Failed to disconnect YouTube:', err);
      toast.error("Failed to disconnect YouTube account.");
    }
  };

  return {
    connected,
    loading,
    connect,
    disconnect,
  };
}